import React from 'react';
import { withRouter } from 'react-router-dom';

import http from 'api/http';
import { boatAPI, inspectionAPI, trailerAPI } from 'api/requests';

import Header from './Header';
import Report from './Report';
import Response from './Response';
import moment from 'moment';
import { STATUS } from 'constants/index';
import ApplicationActions from 'Components/ApplicationActions';
import { ROLES } from 'constants/roles';

const defaultState = {
  report: {
    id: '',
    description: '',
    status: '',
    year: '',
    inspectionDate: '',
    models: [],
    documents: [],
    yearList: [],
    errors: {},
    modelList: [],
    manufacturerAddressesWithCurrent: [],
    address: ''
  },
  response: {
    varDocuments: [],
    error: false
  },
  requestInProcess: false,
};

@withRouter
export default class Inspection extends React.Component {
  constructor(props) {
    super(props);

    defaultState.report.errors = {};
    this.state = {...defaultState};

    this.reportFileIdsToDelete = [];
    this.responseFileIdsToDelete = [];
  }

  componentDidMount() {
    const {user, match} = this.props;
    const {inspectionId} = match.params;

    if (isFinite(inspectionId)) {
      this.setState({requestInProcess: true}, () => {
        http.get(inspectionAPI.inspectionDetails(inspectionId))
          .then(({data: report}) => {
            const $$getMethod = () => {
              if (user.getActiveRole() === ROLES.BOAT_MANUFACTURER.serverName) {
                return boatAPI.modelYears;
              }

              if (user.getActiveRole() === ROLES.TRAILER_MANUFACTURER.serverName) {
                return trailerAPI.getPermissibleModelYears;
              }
            };

            const response = {...defaultState.response, ...report.response};
            delete report.response;
            report.inspectionDate = moment(report.inspectionDate).local();

            http.get($$getMethod())
              .then(({data: yearList}) => {
                this.setState({
                  report: {...defaultState.report, ...report, yearList},
                  response,
                  requestInProcess: false
                }, () => {
                  this.handleYearSelect(this.state.report.year, false);
                });
              });
          });
      });
    }
  }

  componentDidUpdate() {
    if (this.props.manufacturerAddresses && !this.state.report.manufacturerAddressesWithCurrent.length && !this.state.requestInProcess) {
      this.updateInspectionAddress();
    }
  }

  updateInspectionAddress = () => {
    const {manufacturerAddresses} = this.props;
    const {report} = this.state;
    const {mnfCity, mnfAddress, mnfCountry, mnfCountryId, mnfPostalCode, mnfState} = this.state.report;

    const currentAddressValue = {
      'mnfCity': mnfCity,
      'mnfAddress': mnfAddress,
      'mnfCountry': mnfCountry,
      'mnfCountryId': mnfCountryId,
      'mnfPostalCode': mnfPostalCode,
      'mnfState': mnfState
    };

    let manufacturerAddressesWithCurrent = [...manufacturerAddresses];

    let address = manufacturerAddresses.find(address => {
      let isSame = true;
      Object.keys(address.value).forEach(addressField => {
        if (address.value[addressField] !== currentAddressValue[addressField]) isSame = false;
      });
      return isSame;
    });

    if (!address) {
      address = {
        name: [
          currentAddressValue.mnfAddress,
          currentAddressValue.mnfCity,
          currentAddressValue.mnfState,
          currentAddressValue.mnfPostalCode,
          currentAddressValue.mnfCountryId,
        ].filter(object => object).join(', '),
        value: {...currentAddressValue},
      };

      if (address.name) {
        manufacturerAddressesWithCurrent.unshift(address);
      } else {
        report.initializedWithoutAddress = true;
        address = '';
      }
    }

    report.manufacturerAddressesWithCurrent = manufacturerAddressesWithCurrent;
    report.address = address;

    this.setState({report});
  };

  handleInputChange = key => value => {
    const {report} = this.state;
    const {markStepAsRedacted} = this.props;

    if (value !== '') delete report.errors[key];
    markStepAsRedacted();

    report[key] = value;

    this.setState({report});
  };

  handleInspectionDateChange = value => {
    const {report} = this.state;
    const {markStepAsRedacted} = this.props;

    if (value !== '') delete report.errors.inspectionDate;
    if (moment().utc().hour(23).minute(59).second(59).isBefore(value)) {
      report.errors.inspectionDate = 'Cannot select Inspection Date in the future';
    }
    markStepAsRedacted();

    report.inspectionDate = value;

    this.setState({report});
  };

  handleYearSelect = (year, isListShouldBeCleared = true) => {
    const {report} = this.state;
    const {markStepAsRedacted, user} = this.props;

    const $$getMethod = year => {
      if (user.getActiveRole() === ROLES.BOAT_MANUFACTURER.serverName) {
        return boatAPI.approvedBoatsByYear(year);
      }

      if (user.getActiveRole() === ROLES.TRAILER_MANUFACTURER.serverName) {
        return trailerAPI.trailersByYear(year, true);
      }
    };

    markStepAsRedacted();

    if (isListShouldBeCleared) {
      report.models = [];
    }

    if (year === '') {
      report.year = year;
      report.modelList = [];
      this.setState({report});
    } else {
      report.year = year;
      delete report.errors.year;

      http.get($$getMethod(year))
        .then(({data: approvedList}) => {
          report.modelList = approvedList.filter(model => {
            return !report.models.find(rm => +model.id === +rm.id);
          });
        })
        .finally(() => {
          this.setState({report});
        });
    }
  };

  handleSelectFromList = model => {
    const {report} = this.state;
    const {markStepAsRedacted} = this.props;

    const selectedModelIndex = report.modelList.findIndex(element => +model.id === +element.id);
    report.modelList.splice(selectedModelIndex, 1);

    delete report.errors.models;
    markStepAsRedacted();

    report.models.push(model);

    this.setState({report});
  };

  handleRemoveFromList = (model, index) => () => {
    const {report} = this.state;
    const {markStepAsRedacted} = this.props;

    report.models.splice(index, 1);

    if (model.status !== STATUS.DEACTIVATED) {
      report.modelList.push(model);
    }

    if (!report.models.length) report.errors.models = 'At least one model needs to be selected.';

    markStepAsRedacted();

    this.setState({report});
  };

  handleReportFileSelect = addedFiles => {
    const {report} = this.state;
    const {markStepAsRedacted} = this.props;

    delete report.errors.documents;

    if (!report.documents) report.documents = [];

    report.documents.push(...addedFiles);

    markStepAsRedacted();

    this.setState({report});
  };

  handleReportFileRemove = fileIndex => () => {
    const {report} = this.state;
    const {markStepAsRedacted} = this.props;

    if (report.documents[fileIndex].id) {
      this.reportFileIdsToDelete.push(report.documents[fileIndex].id);
    }

    report.documents.splice(fileIndex, 1);
    if (!report.documents.length) {
      report.errors.documents = true;
    }

    markStepAsRedacted();

    this.setState({report});
  };

  handleResponseFileSelect = addedFiles => {
    const {response} = this.state;
    const {markStepAsRedacted} = this.props;

    if (!response.varDocuments) response.varDocuments = [];
    response.varDocuments.push(...addedFiles);
    response.error = false;

    markStepAsRedacted();

    this.setState({response});
  };

  handleResponseFileRemove = fileIndex => () => {
    const {response} = this.state;
    const {markStepAsRedacted} = this.props;

    if (response.varDocuments && !response.varDocuments.length) {
      response.error = true;
    }

    if (response.varDocuments[fileIndex].id) {
      this.responseFileIdsToDelete.push(response.varDocuments[fileIndex].id);
    }

    response.varDocuments.splice(fileIndex, 1);
    markStepAsRedacted();

    this.setState({response});
  };

  handleNotesChange = notes => {
    this.setState(prevState => ({response: {...prevState.response, notes}}));
  };

  _handleUpdateClick = async () => {
    const {report} = this.state;
    const {handleUpdate} = this.props;

    const reportResult = await this._updateReport();
    const responseResult = await this._updateResponse();

    if (report.status === STATUS.COMPLETE) {
      if (report.vresponseRequired && responseResult === null) {
        return this.setState(prevState => ({response: {...prevState.response, error: true}}));
      }
    }

    if (Object.keys(report.errors).length) {
      return null;
    }

    const resultUpdate = async () => {
      const result = {};

      if (reportResult) result.report = await reportResult();
      if (responseResult) result.response = await responseResult();

      return result;
    };

    handleUpdate(resultUpdate);
  };

  handleSaveAndSubmit = ({ isSubmitInspectionShown, isSubmitVariationShown }) => async () => {
    const {report, response} = this.state;
    const {handleSaveAndSubmit} = this.props;

    const reportResult = await this._updateReport();
    const responseResult = await this._updateResponse();

    if (report.status === STATUS.COMPLETE) {
      if (report.vresponseRequired && responseResult === null) {
        return this.setState(prevState => ({response: {...prevState.response, error: true}}));
      }
    }

    if (Object.keys(report.errors).length) {
      return null;
    }

    const resultUpdate = async () => {
      const result = {};

      if (reportResult) result.report = await reportResult();
      if (responseResult) result.response = await responseResult();

      if (isSubmitInspectionShown) {
        result.entry = await http.patch(inspectionAPI.resubmitReport(report.id));
      }

      if (isSubmitVariationShown) {
        result.entry = await http.patch(inspectionAPI.submitVarianceResponse(response.id));
      }

      return result;
    };

    handleSaveAndSubmit(resultUpdate);
  };

  _updateReport = async () => {
    const {id, status, year, description, inspectionDate, documents, notes, models,
      address, initializedWithoutAddress} = this.state.report;
    const {user} = this.props;

    const isUserManufacturer = (!user.isInternal() && !user.isInspector());

    const $$getRequiredFields = () => {
      const fields = [];

      if (user.isInternal()) {
        fields.push('description');

        if (status === STATUS.INCOMPLETE) {
          fields.push('year', 'inspectionDate', 'models');
          if (id) fields.push('documents');
        }
      } else if (user.isInspector()) {
        if (status === STATUS.INCOMPLETE) {
          fields.push('year', 'inspectionDate', 'models', 'documents');
        }

        if (status === STATUS.PENDING) {
          fields.push('description');
        }
      }

      if (!initializedWithoutAddress) fields.push('address');

      return fields;
    };

    const $$validateReport = async () => {
      const {report} = this.state;
      const fields = $$getRequiredFields();

      if (!fields.length) return null;

      let hasErrors = !!Object.keys(report.errors).length;

      for (let field of fields) {
        const data = this.state.report[field];

        if (field === 'models' && !data.length) {
          report.errors.models = 'At least one model needs to be selected.';
        }

        if (data.length !== undefined ? !data.length : !data) {
          report.errors[field] = true;
          hasErrors = true;
        }
      }

      if (hasErrors) {
        this.setState({report}, () => null);
        return true
      }

      return null
    };

    const validationResult = await $$validateReport();

    if (validationResult === null && !isUserManufacturer) {
      const requestJson = {
        inspectionDate: inspectionDate.utc(),
        year,
        description,
        notes,
        ...address.value
      };

      requestJson.modelIds = [
        ...models.map(model => model.id)
      ];

      if (this.reportFileIdsToDelete.length) {
        requestJson.fileIdsToDelete = [...this.reportFileIdsToDelete];
      }

      return async () => {
        const data = new FormData();
        data.append('requestJson', JSON.stringify(requestJson));
        if (documents && documents.length) {
          documents.filter(file => !file.created).forEach(file => {
            data.append('reports', file);
          });
        }
        return http.post(inspectionAPI.inspectionDetails(id), data)
          .then(({data}) => {
            this.reportFileIdsToDelete = [];
            return data;
          })
          .catch(() => {
            return null;
          });
      };
    } else {
      return null;
    }
  };

  _updateResponse = async () => {
    const {response} = this.state;
    const {status, vresponseRequired} = this.state.report;
    const {user} = this.props;

    const $$validateResponse = async () => {
      if (user.isInspector()) return null;

      if (status === STATUS.COMPLETE && vresponseRequired) {
        if (response.status !== STATUS.INCOMPLETE) {
          if (user.isInternal()) return true;
          else return null;
        } else {
          if ((!response.varDocuments || !response.varDocuments.length) && !user.isInternal()) {
            return null;
          } else {
            return true;
          }
        }
      } else {
        return false;
      }
    };

    const validationResult = await $$validateResponse();

    if (validationResult) {
      const requestJson = {
        id: response.id,
        notes: response.notes || ''
      };

      if (this.responseFileIdsToDelete.length) {
        requestJson.fileIdsToDelete = [...this.responseFileIdsToDelete];
        this.responseFileIdsToDelete = [];
      }

      return async () => {
        const data = new FormData();
        data.append('requestJson', JSON.stringify(requestJson));

        if (response.varDocuments && response.varDocuments.length) {
          response.varDocuments.filter(file => !file.created).forEach(file => {
            data.append('reports', file);
          });
        }

        return http.post(inspectionAPI.saveVarianceResponse, data)
          .then(({data}) => {
            return data;
          })
          .catch(() => {
            return null;
          });
      };
    } else {
      return validationResult;
    }
  };

  render() {
    const {report, response, requestInProcess} = this.state;
    const {getReadableStatus, handleGoBackClick, markStepAsRedacted, user, match, getDetailsLink} = this.props;
    const {inspectionId} = match.params;

    const showResponse = report.status !== STATUS.INCOMPLETE && report.vresponseRequired;

    const isSubmitInspectionShown = report.status === STATUS.INCOMPLETE;
    const isSubmitVariationShown = report.status === STATUS.COMPLETE && response.status === STATUS.INCOMPLETE;

    return (
      <section className='scene__scene-wrapper'>
        <section className='scene__content-wrapper'>
          <Header
            report={report}
            response={response}
            user={user}
            readableStatus={getReadableStatus({ ...report, response })}
            handleGoBackClick={handleGoBackClick}
          />
          {!requestInProcess && (
            <React.Fragment>
              <Report
                report={report}
                user={user}
                getDetailsLink={getDetailsLink}
                inspectionId={inspectionId}
                handleInputChange={this.handleInputChange}
                handleInspectionDateChange={this.handleInspectionDateChange}
                handleYearSelect={this.handleYearSelect}
                handleSelectFromList={this.handleSelectFromList}
                handleRemoveFromList={this.handleRemoveFromList}
                handleFileSelect={this.handleReportFileSelect}
                handleFileRemove={this.handleReportFileRemove}
                markStepAsRedacted={markStepAsRedacted}
              />

              {showResponse && (
                <Response
                  response={response}
                  user={user}
                  handleFileSelect={this.handleResponseFileSelect}
                  handleFileRemove={this.handleResponseFileRemove}
                  handleNotesChange={this.handleNotesChange}
                />
              )}
              <ApplicationActions
                backButtonLabel='Cancel'
                saveButtonLabel='Save'
                saveButtonAction={this._handleUpdateClick}
                backButtonAction={handleGoBackClick}
                {...((isSubmitInspectionShown || isSubmitVariationShown) && {
                  saveAndSubmitButtonLabel: 'Save And Submit',
                  saveAndSubmitAction: this.handleSaveAndSubmit({ isSubmitInspectionShown, isSubmitVariationShown }),
                })}
              />
            </React.Fragment>
          )}
        </section>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/Inspection/Edit/index.jsx